// src/views/Employees/Modal/EmployeeModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { modeConstants } from "../constants";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import { userActions } from "../redux/actions";

const DEFAULT_VALUES = {
  name: "",
  email: "",
  phone_number: "",
  is_alert_allowed: false,
  permissions: [],
};

export default function UserModal({
  show,
  onHide,
  mode, // "add" | "edit" | "view"
}) {
  const [countryCode, setCountryCode] = useState("gb");
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const [profilePicFile, setProfilePicFile] = useState(null);
  const [previewPic, setPreviewPic] = useState("");
  const initialData = useSelector((s) => s.userReducer.formData);
  const permissionsData = useSelector((s) => s.userReducer.permissions);
  // load initial data in edit/view
  useEffect(() => {
    if (!show) return;

    if (!permissionsData || permissionsData.length === 0) {
      dispatch(userActions.getPermissions());
    }

    if (isAdd) {
      reset(DEFAULT_VALUES);
      setProfilePicFile(null);
      setPreviewPic("");
      return;
    }

    if (initialData && (isEdit || isView)) {
      const rawPerms = initialData.permissions ?? initialData.user_permissions ?? [];
      const permIds = Array.isArray(rawPerms)
        ? rawPerms.map((p) => String(p.permission_id ?? p.id ?? p))
        : [];

      reset({
        name: initialData.name ?? "",
        email: initialData.email ?? "",
        phone_number: String(initialData.phone_number ?? "") ?? "",
        is_alert_allowed: initialData.is_alert_allowed ? true : false,
        permissions: permIds,
      });
    }

    // show existing image if present
    if (initialData.pic_url) {
      setPreviewPic(process.env.REACT_APP_STORAGE_URL + initialData.pic_url);
    } else {
      setPreviewPic("");
      setProfilePicFile(null);
    }
  }, [show, isAdd, isEdit, isView, initialData, permissionsData, reset, dispatch]);

  const onSubmit = async (values) => {
    const fd = new FormData();
    fd.append("name", values.name || "");
    fd.append("email", values.email || "");
    fd.append("phone_number", values.phone_number || "");
    fd.append("is_alert_allowed", values.is_alert_allowed ? 1 : 0 || 0);

    (values.permissions || []).forEach((id, idx) => {
      fd.append(`permissions[${idx}]`, String(id));
    });

    // files
    if (profilePicFile instanceof File) {
      fd.append("pic", profilePicFile, profilePicFile.name);
    }
    // choose endpoint
    if (isEdit && initialData?.id) {
      dispatch(userActions.updateUser(fd, initialData?.id, onHide));
    } else {
      dispatch(userActions.createUser(fd, onHide));
    }
  };

  const title = isAdd ? "Add User" : isEdit ? "Edit User" : "View User";

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-3">
              {/* LEFT SIDE: all text/date fields */}
              <div className="col-lg-8 pe-lg-5">
                <div className="row g-3">
                  {/* name + email */}
                  <div className="col-12 col-md-6">
                    <div className="form-floating">
                      <input
                        id="name"
                        className="form-control"
                        {...register("name", {
                          required: "Full name is required",
                        })}
                      />
                      <label htmlFor="name">Full Name</label>
                    </div>
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-floating">
                      <input
                        id="email"
                        type="email"
                        className="form-control"
                        {...register("email", {
                          required: "Email is required",
                        })}
                      />
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                    </div>
                    {errors.email && (
                      <small className="text-danger">
                        {errors.email.message}
                      </small>
                    )}
                  </div>

                  {/* phone + permissions */}
                  <div className="col-12 col-md-6">
                    <label
                      htmlFor="phone_number"
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      Phone Number
                    </label>
                    <Controller
                      name="phone_number"
                      control={control}
                      rules={{
                        required: "The field is required",
                        validate: (val) =>
                          (val && val.length >= 8) || "Invalid number",
                      }}
                      render={({ field: { value, onChange } }) => (
                        <PhoneInput
                          country={countryCode}
                          value={(value ?? "").toString()}
                          onChange={(val, country) => {
                            onChange(val);
                            if (country?.countryCode)
                              setCountryCode(country.countryCode);
                          }}
                          inputProps={{
                            name: "phone_number",
                            required: true,
                            autoFocus: false,
                          }}
                          placeholder="Enter phone number"
                          enableSearch
                          disableSearchIcon={false}
                          inputStyle={{ width: "100%" }}
                          buttonStyle={{
                            borderTopLeftRadius: 6,
                            borderBottomLeftRadius: 6,
                          }}
                          containerStyle={{ width: "100%" }}
                          disabled={isView}
                        />
                      )}
                    />
                    {errors.phone_number && (
                      <small className="text-danger">
                        {errors.phone_number.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <label
                      htmlFor="permissions"
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      Permissions
                    </label>
                    <Controller
                      name="permissions"
                      control={control}
                      rules={{
                        required: "Please select at least one permission",
                      }}
                      render={({ field }) => {
                        const selectedValues = (field.value || []).map((val) =>
                          typeof val === "object"
                            ? String(val.permission_id ?? val.id ?? val)
                            : String(val)
                        );
                        return (
                          <Select
                            {...field}
                            id="permissions"
                            isMulti
                            classNamePrefix="react-select"
                            options={permissionsData.map((r) => ({
                              value: String(r.id),
                              label: r.display_name,
                            }))}
                            value={permissionsData
                              .filter((r) =>
                                selectedValues.includes(String(r.id))
                              )
                              .map((r) => ({
                                value: String(r.id),
                                label: r.display_name,
                              }))}
                            onChange={(selected) =>
                              field.onChange(
                                selected
                                  ? selected.map((opt) => String(opt.value))
                                  : []
                              )
                            }
                            placeholder="Select Permissions..."
                            isDisabled={isView}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "46px",
                                maxHeight: "120px",
                                overflowY: "auto",
                                borderColor: state.isFocused
                                  ? "#86b7fe"
                                  : "#ced4da",
                                boxShadow: state.isFocused
                                  ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                                  : "none",
                                "&:hover": { borderColor: "#86b7fe" },
                                borderRadius: "6px",
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#eef2f7",
                                borderRadius: "4px",
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: "#333",
                                fontSize: "12px",
                                fontWeight: "500",
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: "#6c757d",
                                ":hover": {
                                  backgroundColor: "#dc3545",
                                  color: "#fff",
                                },
                              }),
                            }}
                          />
                        );
                      }}
                    />
                    {errors.permissions && (
                      <small className="text-danger">
                        {errors.permissions.message}
                      </small>
                    )}
                  </div>
                  {/* <div className="col-md-6 d-flex align-items-center">
                    <div className="form-check">
                      <input
                        id="is_alert_allowed"
                        type="checkbox"
                        className="form-check-input"
                        {...register("is_alert_allowed")}
                      />
                      <label
                        htmlFor="is_alert_allowed"
                        className="form-check-label ms-2"
                      >
                        Can Receives Alerts
                      </label>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* RIGHT SIDE: file uploads */}
              <div className="col-lg-4 right-panel ps-5 pe-5">
                {/* profile pic */}
                <div className="mb-3 text-center">
                  <div
                    style={{
                      width: 110,
                      height: 110,
                      borderRadius: "50%",
                      background: "#eef5ff",
                      overflow: "hidden",
                      margin: "0 auto 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {previewPic ? (
                      <img
                        src={previewPic}
                        alt="profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <i
                        className="bi bi-person-fill"
                        style={{ fontSize: 56 }}
                      />
                    )}
                  </div>
                  {!isView && (
                    <div className="form-floating">
                      <input
                        id="pp"
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setProfilePicFile(f || null);
                          if (f) {
                            const url = URL.createObjectURL(f);
                            setPreviewPic(url);
                          }
                        }}
                      />
                      <label htmlFor="pp">
                        {" "}
                        Profile Pic
                        {/* <span className="text-danger fs-5">*</span> */}
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </fieldset>

          {/* footer */}
          <div className="d-flex justify-content-end footer-modal">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="btn-footer"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create User"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
